import { createTenantPeopleSource } from "@playbookai/api-clients";
import type { PeopleSource, ResolvedPerson } from "@playbookai/types";
import {
  resolveDirectoryPeople,
  searchDirectoryPeople,
} from "./people-directory-api.js";

/**
 * The tenant people directory (PB-138) as the default `PeopleSource`
 * (assign-person consistency): grant mode `none`, an empty roster, the
 * any-word tenant search, and `resolve` with `includeFormer` so a stored
 * person who left the workspace still has a name and reads "Not active".
 *
 * Built on `people-directory-api`, the CSRF-aware transport every shell
 * person field already uses. AppLayout provides it to every page; a canvas
 * wraps its own source over it, and a shell place with its own list builds
 * its own with `createTenantPeopleSource`.
 */
export function createShellTenantPeopleSource(
  currentUserId?: string,
): PeopleSource {
  return createTenantPeopleSource({
    search: searchTenantPeople,
    resolve: resolveTenantPeople,
    ...(currentUserId ? { currentUserId } : {}),
  });
}

/** One page of the tenant search, in the shape `createTenantPeopleSource` takes. */
export async function searchTenantPeople(
  query: string,
  cursor: string | undefined,
  signal: AbortSignal,
): Promise<{
  readonly items: readonly {
    readonly id: string;
    readonly name: string;
    readonly email: string | null;
  }[];
  readonly nextCursor: string | null;
}> {
  const page = await searchDirectoryPeople({
    query,
    ...(cursor ? { cursor } : {}),
    signal,
  });
  return {
    items: page.items.map((person) => ({
      id: person.userId,
      name: person.displayName,
      email: person.primaryEmail,
    })),
    nextCursor: page.nextCursor,
  };
}

const RESOLVE_BATCH = 50;
/* The resolve route refuses a whole batch over one id that is not a uuid, and
   a stored id need not be one (fixtures, an import). Such an id names nobody
   here, so it is left out and its chip shows the stored text. */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Names and active state for stored ids, in server-sized batches. */
export async function resolveTenantPeople(
  ids: readonly string[],
): Promise<readonly ResolvedPerson[]> {
  const unique = [...new Set(ids)].filter((id) => UUID_RE.test(id));
  const people: ResolvedPerson[] = [];
  for (let index = 0; index < unique.length; index += RESOLVE_BATCH) {
    const batch = await resolveDirectoryPeople(
      unique.slice(index, index + RESOLVE_BATCH),
    );
    for (const person of batch) {
      people.push({
        id: person.userId,
        name: person.displayName,
        active: person.status === "active",
      });
    }
  }
  return people;
}
